import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { DateUtils, Field, Text } from '@pbt/pbt-ui-components'
import { formatMoney } from '@pbt/pbt-ui-components/src/utils/numberUtils'

import { getFinanceInvoice } from '~/store/reducers/finance'
import { Payment } from '~/types'

import { PaymentInvoiceRefundPaymentAmountInput } from './PaymentInvoiceRefundAmountInput'

const useStyles = makeStyles(
  (theme) => ({
    radioLabel: {
      fontSize: '1.6rem',
      color: theme.colors.secondaryText,
    },
    labelRoot: {
      margin: 0,
      height: 30,
    },
    radio: {
      padding: 0,
      marginRight: theme.spacing(1),
    },
  }),
  { name: 'PaymentDepositRefundPaymentSelection' },
)

interface PaymentDepositRefundPaymentSelectionProps {
  assignedInvoiceId: string
  fields: {
    invoiceRefundMethod: Field
    notes: Field
    refundingValue: Field
  }
  invoiceAmount: number
  isLoading: boolean
  onSetRefundPaymentId: (id: string) => void
  refundPaymentId: string
}

export const PaymentDepositRefundPaymentSelection = ({
  assignedInvoiceId,
  fields,
  isLoading,
  onSetRefundPaymentId,
  refundPaymentId,
  invoiceAmount,
}: PaymentDepositRefundPaymentSelectionProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Errors', 'Invoices', 'Validations'])
  const estimate = useSelector(getFinanceInvoice(assignedInvoiceId))
  const estimatePayments =
    estimate?.payments?.filter((p) => p.refundAvailable) ?? ([] as Payment[])

  const { invoiceRefundMethod, refundingValue, notes } = fields

  useEffect(() => {
    if (estimatePayments.length === 1) {
      onSetRefundPaymentId(estimatePayments[0].id)
    }
  }, [estimatePayments])

  if (estimatePayments.length === 0) {
    return <></>
  }

  return estimatePayments.length === 1 ? (
    <PaymentInvoiceRefundPaymentAmountInput
      fields={{ invoiceRefundMethod, notes }}
      initialValue={refundingValue.initialValue}
      isLoading={isLoading}
      maxRefund={Math.min(
        estimatePayments[0].amount ?? Infinity,
        invoiceAmount,
      )}
      onSetRefundValue={(newValue) => refundingValue.setValue(newValue)}
    />
  ) : (
    <>
      <Text variant="h4">{t('Common:PAYMENTS.REFUND_TO')}</Text>
      <RadioGroup
        aria-label="paymentTypes"
        name="payments"
        value={refundPaymentId}
        onChange={(_, value) => {
          onSetRefundPaymentId(value)
        }}
      >
        {estimatePayments.map((payment: Payment) => (
          <Grid key={payment.id}>
            <FormControlLabel
              classes={{
                root: classes.labelRoot,
                label: classes.radioLabel,
              }}
              control={<Radio className={classes.radio} />}
              label={`${payment.paymentMethod} - ${DateUtils.formatDate(
                payment.creationDate,
              )}`}
              value={payment.id}
            />
            <Text pl={4} variant="lowAccent2">
              {t('Invoices:MAX_REFUND_ALLOWED', {
                amount: formatMoney(payment.amount ?? invoiceAmount),
              })}
            </Text>
            {refundPaymentId === payment.id && (
              <>
                <PaymentInvoiceRefundPaymentAmountInput
                  fields={{ invoiceRefundMethod, notes }}
                  initialValue={refundingValue.initialValue}
                  isLoading={isLoading}
                  maxRefund={Math.min(
                    payment.amount ?? Infinity,
                    invoiceAmount,
                  )}
                  onSetRefundValue={(newValue) =>
                    refundingValue.setValue(newValue)
                  }
                />
              </>
            )}
          </Grid>
        ))}
      </RadioGroup>
    </>
  )
}
